import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ASSISTANT_AUDIO_TOKENS_PER_SECOND,
  USER_AUDIO_TOKENS_PER_SECOND,
  addUsage,
  costBreakdown,
  costTotal,
  emptyUsage,
  parseRealtimeUsage,
} from "../components/assistant/voiceCost";
import {
  isRealUserInterrupt,
  mergeFunctionCalls,
  shouldNudgeAnnouncement,
  transcriptFromResponse,
} from "../components/assistant/voiceTurn";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HEARTBEAT_MS = 30000;
const RESPONSE_RETRY_MS = 450;
const RESPONSE_RETRY_MAX = 4;
const FOLLOWUP_RETRY_MAX = 2;

export const VOICE_END_MESSAGES = {
  max_duration: "Hai raggiunto la durata massima della chat vocale.",
  feature_disabled: "La chat vocale è stata disattivata dall'amministratore.",
  voice_budget_exceeded: "Il budget mensile della chat vocale è esaurito.",
  admin: "La sessione è stata chiusa dall'amministratore.",
  replaced: "La chat vocale è stata aperta in un'altra finestra.",
  abandoned: "La sessione è scaduta per inattività.",
  connection_lost: "Connessione audio persa.",
};

function errorDetail(err, fallback) {
  const detail = err?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

function micErrorMessage(err) {
  const name = err?.name || "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "Accesso al microfono negato. Consentilo dalle impostazioni del browser e riprova.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "Nessun microfono trovato su questo dispositivo.";
  }
  if (name === "NotReadableError") {
    return "Il microfono è già in uso da un'altra applicazione.";
  }
  return "Impossibile accedere al microfono.";
}

function readLevel(analyser, buffer) {
  if (!analyser || !buffer) return 0;
  analyser.getByteTimeDomainData(buffer);
  let sum = 0;
  for (let i = 0; i < buffer.length; i += 1) {
    const v = (buffer[i] - 128) / 128;
    sum += v * v;
  }
  return Math.min(1, Math.sqrt(sum / buffer.length) * 3.2);
}

function freshCost() {
  return {
    usage: emptyUsage(),
    localCost: 0,
    serverCost: 0,
    serverBreakdown: null,
    userPendingSeconds: 0,
    userSpeechStart: null,
    assistantEstimateStart: null,
  };
}

/**
 * Chat vocale Realtime (WebRTC) dell'assistente. Nessuna trascrizione: solo stati, livelli audio,
 * schede di conferma e costo live (se abilitato dal super admin).
 */
export default function useRealtimeVoice({ token, onSessionEnd }) {
  const [lifecycle, setLifecycle] = useState("idle"); // idle | connecting | active | ended | error
  const [phase, setPhase] = useState("listening"); // listening | thinking | speaking
  const [executing, setExecuting] = useState(false);
  const [actionPending, setActionPending] = useState(false);
  const [error, setError] = useState(null);
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [maxSeconds, setMaxSeconds] = useState(null);
  const [actions, setActions] = useState([]);
  const [sessionInfo, setSessionInfo] = useState(null);

  const tokenRef = useRef(token);
  const onEndRef = useRef(onSessionEnd);
  const rt = useRef({});
  const costRef = useRef(freshCost());

  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { onEndRef.current = onSessionEnd; }, [onSessionEnd]);

  const authHeaders = () => ({ headers: { Authorization: `Bearer ${tokenRef.current}` } });

  const sendEvent = useCallback((event) => {
    const dc = rt.current.dc;
    if (dc && dc.readyState === "open") dc.send(JSON.stringify(event));
  }, []);

  const requestResponseRef = useRef(() => {});
  const settleTurnRef = useRef(() => {});

  const releaseFollowup = useCallback(() => {
    rt.current.followupDue = false;
  }, []);

  const armResponseWatchdog = useCallback(() => {
    const r = rt.current;
    clearTimeout(r.responseWatch);
    r.responseWatch = setTimeout(() => {
      const cur = rt.current;
      if (cur.stopping || !cur.queuedResponse) return;
      cur.responseRetries = (cur.responseRetries || 0) + 1;
      if (cur.responseRetries > RESPONSE_RETRY_MAX) {
        cur.queuedResponse = false;
        cur.responseActive = false;
        cur.followupDue = false;
        setActionPending(false);
        setExecuting(false);
        setPhase("listening");
        return;
      }
      cur.responseActive = false;
      requestResponseRef.current();
    }, RESPONSE_RETRY_MS);
  }, []);

  const requestResponse = useCallback(() => {
    const r = rt.current;
    if (r.stopping) return;
    if (r.responseActive) {
      r.queuedResponse = true;
      armResponseWatchdog();
      return;
    }
    r.queuedResponse = false;
    r.responseActive = true;
    r.heardAudioThisResponse = false;
    sendEvent({ type: "response.create" });
  }, [armResponseWatchdog, sendEvent]);

  requestResponseRef.current = requestResponse;

  const notifyModel = useCallback((text) => {
    sendEvent({
      type: "conversation.item.create",
      item: { type: "message", role: "system", content: [{ type: "input_text", text }] },
    });
    requestResponse();
  }, [sendEvent, requestResponse]);

  const updateAction = useCallback((id, patch) => {
    setActions((prev) => prev.map((a) => (a.action.id === id ? { ...a, ...patch } : a)));
  }, []);

  const addAction = useCallback((action) => {
    if (!action?.id) return;
    rt.current.actionCreatedAt?.set(action.id, Date.now());
    setActions((prev) => (prev.some((a) => a.action.id === action.id) ? prev : [...prev, { action, status: "pending" }]));
  }, []);

  const teardownMedia = useCallback(() => {
    const r = rt.current;
    clearInterval(r.heartbeatTimer);
    clearInterval(r.tickTimer);
    clearTimeout(r.responseWatch);
    clearTimeout(r.transcriptWait);
    if (r.onPageHide) window.removeEventListener("pagehide", r.onPageHide);
    try { r.dc?.close(); } catch { /* già chiuso */ }
    try { r.pc?.getSenders().forEach((s) => s.track?.stop()); } catch { /* ignorato */ }
    try { r.pc?.close(); } catch { /* già chiuso */ }
    r.micStream?.getTracks().forEach((t) => t.stop());
    if (r.audioEl) {
      r.audioEl.pause();
      r.audioEl.srcObject = null;
    }
    try { r.audioCtx?.close(); } catch { /* già chiuso */ }
    r.dc = null;
    r.pc = null;
  }, []);

  const stop = useCallback(async (reason = "user", notice = null) => {
    const r = rt.current;
    if (r.stopping || !r.started) return;
    r.stopping = true;
    teardownMedia();
    const localDuration = r.startedAt ? Math.round((Date.now() - r.startedAt) / 1000) : 0;
    let summary = { duration_seconds: localDuration };
    if (r.sessionId) {
      try {
        const res = await axios.post(
          `${API_URL}/api/assistant/voice/sessions/${r.sessionId}/end`,
          { reason },
          authHeaders()
        );
        summary = { ...summary, ...(res.data || {}) };
      } catch {
        /* il backend chiude comunque le sessioni abbandonate */
      }
    }
    const endReason = summary.end_reason || reason;
    const message = notice || VOICE_END_MESSAGES[endReason] || null;
    setLifecycle((prev) => (prev === "error" ? prev : "ended"));
    onEndRef.current?.({
      ...summary,
      reason: endReason,
      notice: message,
      show_cost: Boolean(r.showCost),
      session_cost_usd: summary.session_cost_usd ?? (r.showCost ? Math.max(costRef.current.localCost, costRef.current.serverCost) : undefined),
    });
  }, [teardownMedia]);

  const recordUsage = useCallback(async (responseId, usage) => {
    const r = rt.current;
    if (!r.sessionId) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/assistant/voice/sessions/${r.sessionId}/usage`,
        { response_id: responseId, usage },
        authHeaders()
      );
      const data = res.data || {};
      if (typeof data.session_cost_usd === "number" && data.session_cost_usd >= costRef.current.serverCost) {
        costRef.current.serverCost = data.session_cost_usd;
        costRef.current.serverBreakdown = data.cost_breakdown || null;
      }
    } catch {
      /* i consumi restano comunque nella stima locale */
    }
  }, []);

  const runTool = useCallback(async (call) => {
    const r = rt.current;
    let args = {};
    try { args = JSON.parse(call.arguments || "{}"); } catch { args = {}; }
    if (call.name === "conferma_azione") {
      const createdAt = r.actionCreatedAt?.get(args.action_id);
      if (createdAt && (r.lastUserSpeechAt || 0) <= createdAt) {
        return JSON.stringify({
          success: false,
          error: "L'utente non ha ancora risposto. Chiedi conferma a voce e attendi un sì esplicito (oppure che prema Conferma sulla scheda).",
        });
      }
    }
    try {
      const res = await axios.post(
        `${API_URL}/api/assistant/voice/sessions/${r.sessionId}/tool`,
        { call_id: call.call_id, name: call.name, arguments: call.arguments || "{}" },
        authHeaders()
      );
      const data = res.data || {};
      if (data.pending_action) addAction(data.pending_action);
      if (data.resolved_action?.id) {
        updateAction(data.resolved_action.id, {
          status: data.resolved_action.outcome,
          message: data.resolved_action.message,
        });
      }
      return data.output || JSON.stringify({ success: false, error: "Nessun risultato." });
    } catch (err) {
      return JSON.stringify({ success: false, error: errorDetail(err, "Errore di comunicazione con il server.") });
    }
  }, [addAction, updateAction]);

  const runTools = useCallback(async (calls) => {
    const r = rt.current;
    r.toolsRunning = (r.toolsRunning || 0) + 1;
    r.followupDue = true;
    setExecuting(true);
    setActionPending(true);
    setPhase("thinking");
    try {
      for (const call of calls) {
        const output = await runTool(call);
        if (r.stopping) return;
        sendEvent({
          type: "conversation.item.create",
          item: { type: "function_call_output", call_id: call.call_id, output },
        });
      }
    } finally {
      r.toolsRunning -= 1;
      if (r.toolsRunning <= 0) setExecuting(false);
    }
    if (r.stopping || (r.toolsRunning || 0) > 0) return;
    requestResponse();
  }, [runTool, sendEvent, requestResponse]);

  const handleResponseDone = useCallback((response) => {
    const r = rt.current;
    const cost = costRef.current;
    clearTimeout(r.responseWatch);
    r.responseActive = false;
    cost.assistantEstimateStart = null;
    cost.userPendingSeconds = 0;
    if (cost.userSpeechStart) cost.userSpeechStart = Date.now();

    if (response?.usage) {
      cost.usage = addUsage(cost.usage, parseRealtimeUsage(response.usage));
      cost.localCost = costTotal(r.pricing, cost.usage);
      recordUsage(response.id, response.usage);
    }

    const transcript = [r.responseTranscript, transcriptFromResponse(response)].filter(Boolean).join(" ").trim();
    const calls = mergeFunctionCalls(response?.output, r.pendingCalls);
    r.pendingCalls = [];

    const userTookOver = isRealUserInterrupt(response?.status, {
      overlappedAssistant: r.speechOverlappedAssistant,
      speechMs: r.lastSpeechMs || 0,
    });
    if (userTookOver && !calls.length) {
      releaseFollowup();
      if (!(r.toolsRunning > 0)) setActionPending(false);
      if (r.queuedResponse) {
        r.queuedResponse = false;
        if (!r.speaking) setPhase("thinking");
        requestResponse();
        return;
      }
      if (!r.speaking) setPhase("listening");
      return;
    }

    if (calls.length) {
      r.followupDue = true;
      r.followupRetries = 0;
      setActionPending(true);
      if (!r.speaking) setPhase("thinking");
      runTools(calls);
      return;
    }

    if (r.queuedResponse) {
      r.queuedResponse = false;
      if (!r.speaking) setPhase("thinking");
      requestResponse();
      return;
    }

    if (r.followupDue) {
      const delivered = r.heardAudioThisResponse && response?.status !== "cancelled";
      if (!delivered && (r.followupRetries || 0) < FOLLOWUP_RETRY_MAX && response?.status !== "failed") {
        r.followupRetries = (r.followupRetries || 0) + 1;
        setActionPending(true);
        setPhase("thinking");
        requestResponse();
        return;
      }
      releaseFollowup();
      if (!(r.toolsRunning > 0)) setActionPending(false);
    }

    const conclude = (text) => {
      const cur = rt.current;
      if (!cur.awaitingTranscriptDecision && cur.turnSettled) return;
      cur.awaitingTranscriptDecision = false;
      cur.turnSettled = true;
      clearTimeout(cur.transcriptWait);
      const said = String(text || cur.responseTranscript || "").trim();
      cur.responseTranscript = "";
      if (
        shouldNudgeAnnouncement(said)
        && (cur.announcementNudges || 0) < 2
        && !cur.followupDue
        && !(cur.toolsRunning > 0)
      ) {
        cur.announcementNudges = (cur.announcementNudges || 0) + 1;
        setActionPending(true);
        setPhase("thinking");
        sendEvent({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [{
              type: "input_text",
              text: `Il turno precedente si è chiuso dopo un annuncio («${said.slice(0, 180)}») senza eseguire l'operazione. `
                + "Se il risultato è già stato detto, non parlare. Altrimenti esegui ora l'operazione nello stesso turno "
                + "e poi comunica solo il risultato, senza ripetere l'annuncio.",
            }],
          },
        });
        requestResponse();
        return;
      }
      if (!(cur.toolsRunning > 0) && !cur.followupDue) setActionPending(false);
      if (!cur.speaking && !cur.followupDue && !(cur.toolsRunning > 0)) setPhase("listening");
    };
    settleTurnRef.current = conclude;

    if (!transcript) {
      r.awaitingTranscriptDecision = true;
      r.turnSettled = false;
      setActionPending(true);
      if (!r.speaking) setPhase("thinking");
      clearTimeout(r.transcriptWait);
      r.transcriptWait = setTimeout(() => {
        if (!rt.current.awaitingTranscriptDecision) return;
        settleTurnRef.current(rt.current.responseTranscript || "");
      }, 800);
      return;
    }

    r.turnSettled = false;
    conclude(transcript);
  }, [recordUsage, releaseFollowup, requestResponse, runTools, sendEvent]);

  const handleEvent = useCallback((raw) => {
    let evt;
    try { evt = JSON.parse(raw.data); } catch { return; }
    const r = rt.current;
    const cost = costRef.current;
    const now = Date.now();
    switch (evt.type) {
      case "input_audio_buffer.speech_started": {
        const busy = Boolean(r.speaking || r.followupDue || r.toolsRunning > 0 || r.awaitingTranscriptDecision);
        r.ignoreThisUtterance = busy;
        r.speechStartedAt = now;
        r.speechOverlappedAssistant = busy;
        if (busy) break;
        r.lastUserSpeechAt = now;
        r.announcementNudges = 0;
        cost.userSpeechStart = now;
        setActionPending(false);
        setPhase("listening");
        break;
      }
      case "input_audio_buffer.speech_stopped":
        r.lastSpeechMs = now - (r.speechStartedAt || now);
        if (r.ignoreThisUtterance) break;
        if (cost.userSpeechStart) cost.userPendingSeconds += (now - cost.userSpeechStart) / 1000;
        cost.userSpeechStart = null;
        setPhase("thinking");
        break;
      case "response.created":
        r.responseActive = true;
        r.responseRetries = 0;
        if (r.awaitingTranscriptDecision) {
          r.awaitingTranscriptDecision = false;
          r.turnSettled = true;
          clearTimeout(r.transcriptWait);
        }
        r.pendingCalls = [];
        r.callNames = {};
        r.responseTranscript = "";
        r.heardAudioThisResponse = false;
        r.speechOverlappedAssistant = false;
        r.lastSpeechMs = 0;
        clearTimeout(r.responseWatch);
        if (r.speaking) cost.assistantEstimateStart = now;
        if (!r.speaking) setPhase("thinking");
        break;
      case "response.output_item.added":
      case "response.output_item.done": {
        const item = evt.item;
        if (item?.type !== "function_call" || !item.call_id) break;
        r.callNames = r.callNames || {};
        if (item.name) r.callNames[item.call_id] = item.name;
        const args = typeof item.arguments === "string" ? item.arguments : "";
        if (item.name && args.trim()) {
          r.pendingCalls = mergeFunctionCalls([], [...(r.pendingCalls || []), item]);
        }
        r.followupDue = true;
        setActionPending(true);
        if (!r.speaking) setPhase("thinking");
        break;
      }
      case "response.function_call_arguments.done": {
        const callId = evt.call_id;
        const name = evt.name || r.callNames?.[callId];
        const args = typeof evt.arguments === "string" ? evt.arguments : "";
        if (!callId || !name || !args.trim()) break;
        r.pendingCalls = mergeFunctionCalls([], [...(r.pendingCalls || []), {
          call_id: callId,
          name,
          arguments: args,
        }]);
        r.followupDue = true;
        setActionPending(true);
        if (!r.speaking) setPhase("thinking");
        break;
      }
      case "response.output_audio_transcript.delta":
      case "response.audio_transcript.delta":
        r.responseTranscript = (r.responseTranscript || "") + (evt.delta || "");
        break;
      case "response.output_audio_transcript.done":
      case "response.audio_transcript.done":
      case "response.content_part.done": {
        const transcript = evt.transcript || evt.part?.transcript;
        if (transcript) r.responseTranscript = transcript;
        if (r.awaitingTranscriptDecision && (r.responseTranscript || "").trim()) {
          clearTimeout(r.transcriptWait);
          settleTurnRef.current(r.responseTranscript);
        }
        break;
      }
      case "output_audio_buffer.started": {
        r.speaking = true;
        r.heardAudioThisResponse = true;
        if (r.responseActive && !cost.assistantEstimateStart) cost.assistantEstimateStart = now;
        if (!(r.toolsRunning > 0)) setActionPending(false);
        setPhase("speaking");
        break;
      }
      case "output_audio_buffer.stopped":
      case "output_audio_buffer.cleared":
        r.speaking = false;
        if (r.toolsRunning > 0 || r.followupDue || r.responseActive || r.awaitingTranscriptDecision) setPhase("thinking");
        else setPhase("listening");
        break;
      case "response.done":
        handleResponseDone(evt.response);
        break;
      case "error": {
        const code = evt.error?.code || "";
        if (code === "conversation_already_has_active_response") {
          r.queuedResponse = true;
          r.responseActive = true;
          armResponseWatchdog();
        } else {
          console.warn("Realtime error", evt.error);
        }
        break;
      }
      default:
        break;
    }
  }, [armResponseWatchdog, handleResponseDone]);

  const start = useCallback(async () => {
    if (rt.current.started && !rt.current.stopping) return;
    rt.current = { actionCreatedAt: new Map(), started: true };
    costRef.current = freshCost();
    setActions([]);
    setError(null);
    setMuted(false);
    setElapsed(0);
    setExecuting(false);
    setActionPending(false);
    setPhase("listening");
    setSessionInfo(null);
    setLifecycle("connecting");
    const r = rt.current;

    const fail = (message) => {
      teardownMedia();
      setError(message);
      setLifecycle("error");
      if (r.sessionId) {
        axios.post(`${API_URL}/api/assistant/voice/sessions/${r.sessionId}/end`, { reason: "error" }, authHeaders()).catch(() => {});
      }
      r.stopping = true;
    };

    if (!navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === "undefined") {
      fail("Il browser non supporta la chat vocale. Usa una versione recente di Chrome, Edge, Firefox o Safari.");
      return;
    }

    const audioEl = new Audio();
    audioEl.autoplay = true;
    r.audioEl = audioEl;
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      try { r.audioCtx = new AudioCtx(); } catch { r.audioCtx = null; }
    }

    let micStream;
    try {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      fail(micErrorMessage(err));
      return;
    }
    if (r.stopping) {
      micStream.getTracks().forEach((t) => t.stop());
      return;
    }
    r.micStream = micStream;
    r.micTrack = micStream.getAudioTracks()[0];

    if (r.audioCtx) {
      try {
        const analyser = r.audioCtx.createAnalyser();
        analyser.fftSize = 512;
        r.audioCtx.createMediaStreamSource(micStream).connect(analyser);
        r.inputAnalyser = analyser;
        r.inputBuf = new Uint8Array(analyser.fftSize);
      } catch { /* livelli non disponibili */ }
    }

    const pc = new RTCPeerConnection();
    r.pc = pc;
    pc.ontrack = (e) => {
      const [stream] = e.streams;
      audioEl.srcObject = stream;
      audioEl.play().catch(() => {});
      if (r.audioCtx && !r.outputAnalyser) {
        try {
          const analyser = r.audioCtx.createAnalyser();
          analyser.fftSize = 512;
          r.audioCtx.createMediaStreamSource(stream).connect(analyser);
          r.outputAnalyser = analyser;
          r.outputBuf = new Uint8Array(analyser.fftSize);
        } catch { /* livelli non disponibili */ }
      }
    };
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === "failed" && !r.stopping) {
        stop("error", VOICE_END_MESSAGES.connection_lost);
      }
    };
    pc.addTrack(r.micTrack, micStream);

    const dc = pc.createDataChannel("oai-events");
    r.dc = dc;
    dc.onmessage = handleEvent;
    dc.onopen = () => {
      if (r.stopping) return;
      r.startedAt = Date.now();
      setLifecycle("active");
      notifyModel("La conversazione è appena iniziata. Presentati come EVA, l'assistente vocale di Affidia, in una frase breve e chiedi come puoi aiutare. Il nome si pronuncia Affidia: scrivilo così, non Affidya.");
      r.tickTimer = setInterval(() => {
        const secs = Math.floor((Date.now() - r.startedAt) / 1000);
        setElapsed(secs);
        if (r.maxSeconds && secs >= r.maxSeconds) stop("max_duration");
      }, 1000);
      r.heartbeatTimer = setInterval(async () => {
        try {
          const res = await axios.post(
            `${API_URL}/api/assistant/voice/sessions/${r.sessionId}/heartbeat`,
            {},
            authHeaders()
          );
          if (res.data?.terminate) stop(res.data.reason || "user");
        } catch (err) {
          if (err?.response?.status === 404) stop("error");
        }
      }, HEARTBEAT_MS);
    };
    dc.onclose = () => {
      if (!r.stopping && r.startedAt) stop("error", VOICE_END_MESSAGES.connection_lost);
    };

    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const res = await axios.post(
        `${API_URL}/api/assistant/voice/sessions`,
        { sdp: offer.sdp },
        authHeaders()
      );
      if (r.stopping) {
        if (res.data?.session_id) {
          axios.post(`${API_URL}/api/assistant/voice/sessions/${res.data.session_id}/end`, { reason: "user" }, authHeaders()).catch(() => {});
        }
        return;
      }
      const data = res.data || {};
      r.sessionId = data.session_id;
      r.maxSeconds = data.max_session_seconds || null;
      r.showCost = Boolean(data.show_cost_to_user);
      r.pricing = data.pricing || null;
      setMaxSeconds(r.maxSeconds);
      setSessionInfo({
        model: data.model,
        voice: data.voice,
        showCost: r.showCost,
        pricing: r.pricing,
      });
      r.onPageHide = () => {
        if (!r.sessionId || r.stopping) return;
        fetch(`${API_URL}/api/assistant/voice/sessions/${r.sessionId}/end`, {
          method: "POST",
          keepalive: true,
          headers: { Authorization: `Bearer ${tokenRef.current}`, "Content-Type": "application/json" },
          body: JSON.stringify({ reason: "page_closed" }),
        }).catch(() => {});
      };
      window.addEventListener("pagehide", r.onPageHide);
      await pc.setRemoteDescription({ type: "answer", sdp: data.answer_sdp });
    } catch (err) {
      fail(errorDetail(err, "Impossibile avviare la chat vocale. Riprova tra qualche istante."));
    }
  }, [handleEvent, notifyModel, stop, teardownMedia]);

  const cancelStart = useCallback(() => {
    const r = rt.current;
    if (r.startedAt) {
      stop("user");
      return;
    }
    r.stopping = true;
    teardownMedia();
    if (r.sessionId) {
      axios.post(`${API_URL}/api/assistant/voice/sessions/${r.sessionId}/end`, { reason: "user" }, authHeaders()).catch(() => {});
    }
    setLifecycle("idle");
  }, [stop, teardownMedia]);

  const toggleMute = useCallback(() => {
    const track = rt.current.micTrack;
    if (!track) return;
    track.enabled = !track.enabled;
    setMuted(!track.enabled);
  }, []);

  const resolveByButton = useCallback(async (action, kind) => {
    if (!action?.id) return;
    updateAction(action.id, { status: kind === "confirm" ? "confirming" : "cancelling" });
    try {
      const res = await axios.post(`${API_URL}/api/assistant/actions/${action.id}/${kind}`, {}, authHeaders());
      const data = res.data || {};
      let status = "failed";
      if (data.cancelled) status = "cancelled";
      else if (data.success) status = "executed";
      const message = data.message || data.error || "";
      updateAction(action.id, { status, message });
      const pressed = kind === "confirm" ? "Conferma" : "Annulla";
      notifyModel(
        `[Evento interfaccia] L'utente ha premuto «${pressed}» sulla scheda «${action.riepilogo || action.label}» `
        + `(action_id ${action.id}). Esito: ${message} Comunica l'esito in una frase breve; non chiamare conferma_azione né annulla_azione per questa azione.`
      );
    } catch (err) {
      updateAction(action.id, { status: "pending" });
      setError(errorDetail(err, "Operazione non riuscita. Riprova."));
    }
  }, [notifyModel, updateAction]);

  const getLevels = useCallback(() => {
    const r = rt.current;
    return {
      input: muted ? 0 : readLevel(r.inputAnalyser, r.inputBuf),
      output: readLevel(r.outputAnalyser, r.outputBuf),
    };
  }, [muted]);

  const getLiveCost = useCallback(() => {
    const r = rt.current;
    const cost = costRef.current;
    const now = Date.now();
    const userSeconds = cost.userPendingSeconds + (cost.userSpeechStart ? (now - cost.userSpeechStart) / 1000 : 0);
    const assistantSeconds = cost.assistantEstimateStart ? (now - cost.assistantEstimateStart) / 1000 : 0;
    const extraUsage = {
      ...emptyUsage(),
      audio_input: Math.round(userSeconds * USER_AUDIO_TOKENS_PER_SECOND),
      audio_output: Math.round(assistantSeconds * ASSISTANT_AUDIO_TOKENS_PER_SECOND),
    };
    const useServer = cost.serverBreakdown && cost.serverCost >= cost.localCost;
    const base = useServer ? cost.serverBreakdown : costBreakdown(r.pricing, cost.usage);
    const extra = costBreakdown(r.pricing, extraUsage);
    const breakdown = Object.fromEntries(
      Object.keys(extra).map((k) => [k, (Number(base?.[k]) || 0) + extra[k]])
    );
    return {
      total: Math.max(cost.localCost, cost.serverCost) + costTotal(r.pricing, extraUsage),
      breakdown,
      usage: addUsage(cost.usage, extraUsage),
      estimating: userSeconds > 0 || assistantSeconds > 0,
    };
  }, []);

  useEffect(() => () => {
    const r = rt.current;
    if (r.started && !r.stopping) {
      if (r.startedAt) stop("user");
      else teardownMedia();
    }
  }, [stop, teardownMedia]);

  const hasPendingAction = actions.some((a) => a.status === "pending");
  let status = lifecycle;
  if (lifecycle === "active") {
    if (phase === "speaking") status = "speaking";
    else if (executing || actionPending) status = "executing";
    else if (phase === "thinking") status = "thinking";
    else status = hasPendingAction ? "awaiting_confirmation" : "listening";
  }

  return {
    status,
    error,
    clearError: () => setError(null),
    muted,
    elapsed,
    maxSeconds,
    actions,
    sessionInfo,
    start,
    stop,
    cancelStart,
    toggleMute,
    confirmAction: (action) => resolveByButton(action, "confirm"),
    cancelAction: (action) => resolveByButton(action, "cancel"),
    getLevels,
    getLiveCost,
  };
}
